import { useMemo, useCallback } from "react";
import {
  Pagination,
  PaginationContent,
  PaginationItem,
  PaginationLink,
  PaginationNext,
  PaginationPrevious,
  PaginationEllipsis,
} from "@/components/ui/pagination";

import { ChevronDown } from "lucide-react";

const TablePagination = ({
  currentPage = 1,
  totalRecords = 0,
  pageSize = 10,
  onPageChange = () => {},
  onPageSizeChange = () => {},
  pageSizeOptions = [10, 25, 50, 100],
}) => {
  // =========================
  // COMPUTE totalPages from totalRecords + pageSize
  // (was defaulting to 1 — Next was always disabled)
  // =========================
  const totalPages = useMemo(
    () => Math.max(1, Math.ceil(totalRecords / pageSize)),
    [totalRecords, pageSize],
  );

  // =========================
  // PAGE GENERATION
  // =========================
  const pages = useMemo(() => {
    const items = [];

    if (totalPages <= 1) return [1];

    const add = (val) => items.push(val);

    if (currentPage > 3) add(1);
    if (currentPage > 4) add("left-ellipsis");

    for (
      let i = Math.max(1, currentPage - 1);
      i <= Math.min(totalPages, currentPage + 1);
      i++
    ) {
      add(i);
    }

    if (currentPage < totalPages - 3) add("right-ellipsis");
    if (currentPage < totalPages - 2) add(totalPages);

    return items;
  }, [currentPage, totalPages]);

  // =========================
  // RECORD RANGE for display  e.g. "1–10 of 47"
  // =========================
  const rangeStart = totalRecords === 0 ? 0 : (currentPage - 1) * pageSize + 1;
  const rangeEnd = Math.min(currentPage * pageSize, totalRecords);

  // =========================
  // HANDLERS
  // =========================
  const handleNext = useCallback(() => {
    if (currentPage < totalPages) onPageChange(currentPage + 1);
  }, [currentPage, totalPages, onPageChange]);

  const handlePrev = useCallback(() => {
    if (currentPage > 1) onPageChange(currentPage - 1);
  }, [currentPage, onPageChange]);

  // =========================
  // UI
  // =========================
  return (
    <div className="mt-5 grid grid-cols-1 lg:grid-cols-3 items-center gap-4">
      {/* LEFT - PAGE SIZE */}
      <div className="flex items-center gap-2 justify-start">
        <span className="text-sm text-slate-500 dark:text-slate-400 whitespace-nowrap">
          Rows per page
        </span>

        <div className="relative">
          <select
            value={pageSize}
            onChange={(e) => onPageSizeChange(Number(e.target.value))}
            className="appearance-none pl-3 pr-8 py-2 text-sm rounded-lg border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 text-slate-700 dark:text-white focus:outline-none focus:ring-2 focus:ring-teal-500"
          >
            {pageSizeOptions.map((size) => (
              <option key={size} value={size}>
                {size}
              </option>
            ))}
          </select>

          <ChevronDown className="w-4 h-4 absolute right-2 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none" />
        </div>
      </div>

      {/* CENTER - PAGINATION */}
      <div className="flex justify-center">
        <Pagination>
          <PaginationContent>
            {/* PREVIOUS */}
            <PaginationItem>
              <PaginationPrevious
                onClick={handlePrev}
                className={`rounded-lg border border-slate-200 dark:border-slate-700 ${
                  currentPage === 1
                    ? "pointer-events-none opacity-50"
                    : "hover:bg-slate-100 dark:hover:bg-slate-800"
                }`}
              />
            </PaginationItem>

            {/* PAGES */}
            {pages.map((page, index) => {
              if (page === "left-ellipsis" || page === "right-ellipsis") {
                return (
                  <PaginationItem key={index}>
                    <PaginationEllipsis />
                  </PaginationItem>
                );
              }

              return (
                <PaginationItem key={page}>
                  <PaginationLink
                    isActive={currentPage === page}
                    onClick={() => onPageChange(page)}
                    className={`rounded-lg min-w-[38px] ${
                      currentPage === page
                        ? "bg-teal-500 text-white border-teal-500 hover:bg-teal-600"
                        : "border border-slate-200 dark:border-slate-700 hover:bg-slate-100 dark:hover:bg-slate-800"
                    }`}
                  >
                    {page}
                  </PaginationLink>
                </PaginationItem>
              );
            })}

            {/* NEXT */}
            <PaginationItem>
              <PaginationNext
                onClick={handleNext}
                className={`rounded-lg border border-slate-200 dark:border-slate-700 ${
                  currentPage >= totalPages
                    ? "pointer-events-none opacity-50"
                    : "hover:bg-slate-100 dark:hover:bg-slate-800"
                }`}
              />
            </PaginationItem>
          </PaginationContent>
        </Pagination>
      </div>

      {/* RIGHT - RECORD INFO */}
      <div className="flex justify-end text-sm text-slate-500 dark:text-slate-400">
        <p>
          Showing{" "}
          <span className="font-semibold text-slate-700 dark:text-slate-200">
            {rangeStart}–{rangeEnd}
          </span>{" "}
          of{" "}
          <span className="font-semibold text-slate-700 dark:text-slate-200">
            {totalRecords}
          </span>{" "}
          records
        </p>
      </div>
    </div>
  );
};

export default TablePagination;
